import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import { useState } from "react";

/**
 * A "set default assignee" button whose popover holds its own picker, Save, and
 * Clear — deliberately independent of the live-writing override picker.
 */
export function ScopeDefaultButton({
  buttonText,
  pickerLabel,
  currentDefault,
  staffOnly,
  saving,
  disabled,
  onSave,
  onClear,
}: {
  buttonText: string;
  pickerLabel: string;
  /** The scope's current default assignee, prefilled into the picker. */
  currentDefault: UserOption | null;
  staffOnly?: boolean;
  saving: boolean;
  /** Blocks opening the popover while the current defaults are still loading. */
  disabled?: boolean;
  onSave: (userId: string) => Promise<void>;
  onClear: () => Promise<void>;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<UserOption[] | null>(null);

  const open = (event: React.MouseEvent<HTMLElement>) => {
    // Re-seed on every open so a stale selection from a previous visit never leaks in.
    setSelected(currentDefault ? [currentDefault] : null);
    setAnchorEl(event.currentTarget);
  };
  const close = () => setAnchorEl(null);

  const selectedUserId = selected && selected.length > 0 ? (selected[0]?.id ?? null) : null;

  const handleSave = async () => {
    // Save is disabled without a selection; guard anyway for safety.
    if (!selectedUserId) return;
    await onSave(selectedUserId);
    close();
  };

  const handleClear = async () => {
    await onClear();
    close();
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color={staffOnly ? "staff" : "primary"}
        disabled={saving || disabled}
        onClick={open}
        sx={{ alignSelf: "flex-start" }}
      >
        {buttonText}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2">{buttonText}</Typography>
          <MultipleUserPicker
            label={pickerLabel}
            multiple={false}
            staffOnly={staffOnly}
            value={selected}
            disabled={saving}
            onChange={setSelected}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {currentDefault ? (
              <Button
                size="small"
                color="error"
                disabled={saving}
                onClick={() => void handleClear()}
              >
                Clear default
              </Button>
            ) : null}
            <Button
              size="small"
              variant="contained"
              disabled={saving || !selectedUserId}
              onClick={() => void handleSave()}
            >
              Save default
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
